import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconProfileProBuyer } from "@/assets/icons/figma/IconProfileProBuyer";
import { useProBuyerViewer } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import {
  rememberProBuyerFilter,
  useRememberProBuyerFilter,
} from "@/features/pro-buyer-price/hooks/useRememberProBuyerFilter";
import {
  PRO_BUYER_URL_PARAM,
  PRO_BUYER_URL_VALUE,
} from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import { getProBuyerCount } from "@/features/pro-buyer-price/utils/pro-buyer-count.utils";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { formatResultCount } from "@/features/search/utils/count-placeholder.utils";
import { Button } from "@/ui/cva/Button";

/**
 * WHO-5953, replaces the WHO-5743 toolbar switch. A full-width strip above the
 * results, for signed-in Pro Buyers only: how many items in this search carry a
 * Pro Buyer price, and one button to show only those or everything again.
 *
 * The state is the URL (`proBuyer=1`). Menu and search links do not carry it, so
 * `useRememberProBuyerFilter` keeps the choice per session and puts it back on the
 * next category, search or Nieuw page until the buyer turns it off (WHO-5953 F1). {count} is the search service's
 * `merchant_trade_discount_enabled` facet, read off the same response as the
 * results: no extra request.
 */
export const ProBuyerSearchStrip = () => {
  const { isProBuyer } = useProBuyerViewer();
  useRememberProBuyerFilter(isProBuyer);

  if (!isProBuyer) {
    return null;
  }

  return <ProBuyerSearchStripContent />;
};

const ProBuyerSearchStripContent = () => {
  const { t } = useTranslation();
  const { query: routerQuery, locale } = useRouter();
  const {
    query,
    navigation: { setParam },
  } = useSearchContext();
  const filterOn = routerQuery[PRO_BUYER_URL_PARAM] === PRO_BUYER_URL_VALUE;
  const count = getProBuyerCount(query.data, filterOn);

  /* Off and nothing to show: no strip. On: always, so the buyer can get out of an empty result. */
  if (!filterOn && !count) {
    return null;
  }

  const formatted = formatResultCount(count ?? 0, locale);

  return (
    <div
      className="flex items-center gap-3 rounded-[6px] bg-brand-100 px-4 py-3 text-brand-500"
      data-testid="pro-buyer-search-strip"
    >
      <IconProfileProBuyer aria-hidden className="size-4 shrink-0" />
      <p className="min-w-0 flex-1 font-body text-sm font-bold">
        {filterOn
          ? t("common:pro-buyer-search-strip-on", {
              defaultValue:
                "You only see the {{count}} items with a Pro Buyer discount",
              replace: { count: formatted },
            })
          : t("common:pro-buyer-search-strip-off", {
              defaultValue:
                "There are {{count}} items with a Pro Buyer discount available",
              replace: { count: formatted },
            })}
      </p>
      <Button
        type="button"
        size="small"
        appearance={filterOn ? "outlined" : "solid"}
        className="min-h-11 shrink-0 whitespace-nowrap md:min-h-0"
        onClick={() => {
          rememberProBuyerFilter(!filterOn);
          setParam(PRO_BUYER_URL_PARAM, filterOn ? null : PRO_BUYER_URL_VALUE);
        }}
        data-testid="pro-buyer-search-strip-button"
      >
        {filterOn
          ? t("common:pro-buyer-search-strip-show-all", "Show all")
          : t("common:pro-buyer-search-strip-show-only", "Show only these")}
      </Button>
    </div>
  );
};
